import React, {createContext, ReactNode, useCallback, useMemo, useState} from "react";
import {Alert, ColorPaletteProp, Snackbar, Typography} from "@mui/joy";
import {CheckIcon, CircleAlert, InfoIcon, TriangleAlertIcon} from "lucide-react";

export const IFeedbackContext = createContext<FeedbackContextState>({})

export type FeedbackContextState = {
    showFeedback?: (value: string, severity: ColorPaletteProp) => void,
    showResultsFeedback?: (notification: Notification) => void,
    hideFeedback?: () => void
}

const FeedbackContext = ({children}) => {
    const [showAlert, setShowAlert] = useState(false)
    const [feedbackMessage, setFeedbackMessage] = useState("")
    const [severity, setSeverity] = useState<ColorPaletteProp>()
    const severityIconMapper: { [key in ColorPaletteProp]?: ReactNode } = {
        danger: <TriangleAlertIcon style={{color: "inherit"}}/>,
        success: <CheckIcon style={{color: "inherit"}}/>,
        warning: <CircleAlert style={{color: "inherit"}}/>,
        primary: <InfoIcon style={{color: "inherit"}}/>,
        neutral: <InfoIcon style={{color: "inherit"}}/>,
    }

    const handleClose = () => {
        setShowAlert(false)
    }

    const showFeedback = useCallback((message: string, severity: ColorPaletteProp) => {
        setFeedbackMessage(message)
        setSeverity(severity)
        setShowAlert(true)
    }, [])

    const hideFeedback = useCallback(() => {
        setShowAlert(false)
    }, [])

    /**
     * Memoized because this value is read by the api service, whose own identity decides when the
     * screens depending on it reload.
     */
    const state = useMemo<FeedbackContextState>(() => ({
        showFeedback,
        hideFeedback
    }), [showFeedback, hideFeedback])

    return <IFeedbackContext.Provider value={state}>
        {<Snackbar size={"lg"} onClose={handleClose} sx={{padding: 0}}
                   open={showAlert}
                   autoHideDuration={severity === "danger" ? 3500 : 2500}
                   anchorOrigin={{
                       vertical: "top",
                       horizontal: "center"
                   }}>

            {<Alert component={null} size={"lg"} sx={{width: "100%"}}
                    color={severity}
                    startDecorator={severityIconMapper[severity]}>
                <Typography level={"title-md"}>
                    {feedbackMessage}
                </Typography>
            </Alert>}
        </Snackbar>}
        {children}
    </IFeedbackContext.Provider>
}

export default FeedbackContext
